import type { ElementType, ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';
import type { Spacings } from '../../css/designTokens/spacing';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';
import type { AlignItemsType } from '../Box/flexTypes';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';

const DEFAULT_ELEMENT = 'div';

export type StackProps = {
  /**
   * Optional children
   */
  children?: ReactNode | undefined;
  /**
   * The gap between each item in the stack. Accepts a [spacing token](/docs/docs-spacing--docs). Can be a [responsive prop](/docs/ui-docs-responsive-props--docs).
   */
  gap?: ResponsiveObject<Spacings> | Spacings;
  /**
   * Vertical is equivalent to `flex-direction: column;`, horizontal is equivalent to `flex-direction: row;`.
   * The default behavior is `vertical`. Can be a [responsive prop](/docs/ui-docs-responsive-props--docs).
   */
  direction?: ResponsiveObject<'horizontal' | 'vertical'> | 'horizontal' | 'vertical';
  /**
   * Controls how items are aligned along the cross-axis.
   * Can be a [responsive prop](/docs/ui-docs-responsive-props--docs).
   *
   * @type 'baseline' | 'center' | 'flex-end' | 'flex-start' | 'stretch' | ResponsiveObject<'baseline' | 'center' | 'flex-end' | 'flex-start' | 'stretch'>
   */
  alignItems?: AlignItemsType | ResponsiveObject<AlignItemsType>;
  /**
   * Padding to apply to the stack container. Use `x` for horizontal (left/right) padding and `y` for vertical (top/bottom) padding.
   * Each value can be a [responsive prop](/docs/ui-docs-responsive-props--docs).
   */
  padding?: {
    x?: ResponsiveObject<Spacings> | Spacings;
    y?: ResponsiveObject<Spacings> | Spacings;
  };
  // This will inherit the jsdoc block from PolymorphicComponent
  renderAs?: ElementType;
};

type StyledStackProps = {
  $direction: 'horizontal' | 'vertical';
  $gap: Spacings;
  $alignItems: NonNullable<StackProps['alignItems']>;
  $paddingX?: Spacings;
  $paddingY?: Spacings;
};

const StyledStack = styled.div<StyledStackProps>`
  display: flex;
  flex-direction: ${({ $direction }) => ($direction === 'horizontal' ? 'row' : 'column')};
  gap: ${({ $gap }) => `var(--wui-${$gap})`};
  align-items: ${({ $alignItems }) => $alignItems};
  ${({ $paddingX }) =>
    $paddingX && `padding-left: var(--wui-${$paddingX}); padding-right: var(--wui-${$paddingX});`}
  ${({ $paddingY }) =>
    $paddingY && `padding-top: var(--wui-${$paddingY}); padding-bottom: var(--wui-${$paddingY});`}
`;

const StackComponent = forwardRef<typeof DEFAULT_ELEMENT, StackProps>(
  (
    {
      renderAs,
      direction = 'vertical',
      gap = 'space-02',
      alignItems = 'stretch',
      padding,
      ...props
    },
    ref,
  ) => {
    const responsiveGap = useResponsiveProp(gap);
    const responsiveDirection = useResponsiveProp(direction);
    const responsiveAlignItems = useResponsiveProp(alignItems);
    const responsivePaddingX = useResponsiveProp(padding?.x);
    const responsivePaddingY = useResponsiveProp(padding?.y);

    return (
      <StyledStack
        ref={ref}
        $alignItems={responsiveAlignItems}
        $direction={responsiveDirection}
        $gap={responsiveGap}
        $paddingX={responsivePaddingX}
        $paddingY={responsivePaddingY}
        as={renderAs ?? DEFAULT_ELEMENT}
        {...props}
      />
    );
  },
);

StackComponent.displayName = 'Stack';

/**
 * Used to layout its children in a vertical or horizontal stack. The gap between each item in the stack can be customized.
 * This allows for layouting children in a consistent way without needing to manually add margins or padding.
 */
// @ts-expect-error makePolymorphic is causing issues with types in R19
export const Stack = makePolymorphic<typeof DEFAULT_ELEMENT, StackProps>(StackComponent);
